import React, { useContext, useMemo } from 'react';
import { ReportHeroModel } from './ReportHero';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import {
  Link,
  MetadataContext,
  RichText,
  SystemFeaturesContext,
  AiSummaryButton,
  UserContext,
  GDAiSummaryButton,
} from '@/components';
import ClockLight from '@/assets/clockLight.svg';
import Listen from '@/assets/listen.svg';
import ArticleTools from './ArticleTools';
import AudioPlayer from './AudioPlayer';
import { UserRole } from '@/types/UserModel';

export type HeroDataType = ReportHeroModel & {
  layout?: 'vertical' | 'horizontal';
  hideAiSummaryBtn?: boolean;
  hideAudioPlayer?: boolean;
  hideGalleryButton?: boolean;
  hideDownloadHubButton?: boolean;
  hideSaveToScrapbookButton?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;


const HeroData = ({
  props,
  className,
  layout = 'horizontal',
  hideAiSummaryBtn = false,
  hideAudioPlayer = false,
  hideGalleryButton = false,
  hideDownloadHubButton = false,
  hideSaveToScrapbookButton = false,
}: HeroDataType) => {
  const { publishedDate, authors, readTime, title, listenTime, stub } = props;
  const systemFeaturesContext = useContext(SystemFeaturesContext);
  const { pageUrl, isStub, isPublicReport, isPublicReportIsGated, isParent } = useContext(MetadataContext);
  const user = useContext(UserContext);
  const t = useTranslations();

  const chipClassName = classNames(
    'chip border-from-bg/10 flex items-center rounded-full border py-2 min-h-[34px]',
    layout === 'vertical' && '[&_.chip-content]:flex-nowrap [&_.chip-content]:whitespace-nowrap',
  );

  return (
    <div
      className={classNames(
        'color-from-bg flex w-full flex-col gap-6 lg:gap-2',
        layout === 'horizontal' ? 'md:flex-row' : 'md:flex-col',
        className,
      )}
    >
      <div
        className={classNames(
          'flex shrink-0 flex-col items-start gap-6 font-medium',
          layout === 'horizontal' ? 'md:w-2/5 lg:w-1/3' : 'w-full',
        )}
      >
        {publishedDate && (
          <p className="body-1 w-auto">
            {t('report.$heroPublishedDate', {
              date: dayjs(publishedDate)?.format('DD MMMM YYYY'),
            })}
          </p>
        )}

        <div
          className={classNames(
            'flex shrink-0 flex-col',
            layout === 'horizontal'
              ? 'items-start gap-6 md:flex-col'
              : 'flex-wrap items-center gap-x-2 gap-y-4 md:flex-row',
          )}
        >
          {authors && authors.length > 0 && (
            <div className={chipClassName}>
              <div className="px-4">{authors.length > 1 ? t('$authorsLabel') : t('$authorLabel')}</div>
              <div className="border-from-bg/10 h-[18px] border-r" />
              <div className="chip-content flex flex-wrap gap-x-3 gap-y-1 px-4 md:w-min xl:w-auto">
                {authors.map((author, index) => {
                  return author.landingPageLink ? (
                    <Link className="whitespace-nowrap hover:underline" key={index} link={author.landingPageLink}>
                      {author.expertName}
                      {index < authors.length - 1 && ','}
                    </Link>
                  ) : (
                    <p className="whitespace-nowrap" key={index}>
                      {author.expertName}
                      {index < authors.length - 1 && ','}
                    </p>
                  );
                })}
              </div>
            </div>
          )}
          {readTime && readTime != '0' && (
            <div className={chipClassName}>
              <ClockLight className="fill-from-bg mx-4 h-6 w-6 shrink-0" />
              <div className="border-from-bg/10 h-[18px] border-r" />
              <div className="chip-content px-4">{t('report.$readTime', { time: readTime })}</div>
            </div>
          )}

          {systemFeaturesContext.aiChatPage && !stub && !isParent && !hideAiSummaryBtn && (
            <GDAiSummaryButton className={chipClassName} title={title} />
          )}

          <ArticleTools
            title={props.title}
            pageUrl={pageUrl}
            thumbnailImage={props.thumbnailImage}
            stub={isStub}
            directory={props.directory?.name}
            readTime={props.readTime}
            publishedDate={props.publishedDate}
            hideGalleryButton={hideGalleryButton}
            hideDownloadHubButton={hideDownloadHubButton}
            hideSaveToScrapbookButton={hideSaveToScrapbookButton}
          />

          {!hideAudioPlayer &&
            ((isPublicReport && !isPublicReportIsGated) || user?.userId || user?.isIpBasedIdentity) && (
              <AudioPlayer chipClassName={chipClassName} />
            )}

          {listenTime && (
            <div className={chipClassName}>
              <Listen className="fill-from-bg mx-4 h-6 w-6" />
              <div className="border-from-bg/10 h-[18px] border-r" />
              <div className="px-4">{t('report.$listenTime', { time: listenTime })}</div>
            </div>
          )}
        </div>
      </div>
      {props.intro && props.intro.length > 0 && (
        <div>
          <RichText animate={false} className="link-color-from-bg [&_.largerText]:font-light" data={props.intro} />
        </div>
      )}
    </div>
  );
};

export default HeroData;
